import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { MobileLayout } from './components/mobile/MobileLayout';
import { MobileHome } from './components/mobile/MobileHome';
import { LandingPageWithAudio } from './components/LandingPageWithAudio';
import SelfTalkAI from './components/SelfTalk';
import { AuthPage } from './components/AuthPage';
import { MainApp } from './components/MainApp';
import { ArchitectureDiagram } from './components/ArchitectureDiagram';
import { ProjectStructure } from './components/ProjectStructure';
import { MobileRecorder } from './components/MobileRecorder';
import { RecordingsLibrary } from './components/RecordingsLibrary';
import { Sitemap } from './components/Sitemap';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { TestDashboard } from './components/TestDashboard';
import { Settings } from './components/Settings';
import TextDrop from './components/TextDrop';
import { useAnalytics } from './utils/analytics';
import { getVersionString } from './version';

function App() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  
  // Initialize analytics tracking
  useAnalytics();

  useEffect(() => {
    // Set document title with version
    document.title = `Selfi ${getVersionString()} - Your Inner Voice, Amplified`;
    
    // Detect if mobile device or small screen
    const checkMobile = () => {
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                     window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobile routes that should use MobileLayout
  const mobileRoutes = ['/', '/record', '/library', '/recordings', '/settings'];
  const isMobileRoute = mobileRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  // Desktop marketing/legacy routes
  const desktopRoutes = ['/welcome', '/coach', '/demo', '/architecture', '/structure'];
  const isDesktopRoute = desktopRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  // Use mobile layout for mobile routes, desktop layout for desktop routes
  if (isMobileRoute) {
    return (
      <MobileLayout showNav={location.pathname !== '/record'}>
        <Routes>
          {/* Mobile App Routes */}
          <Route path="/" element={<MobileHome />} />
          <Route path="/record" element={<MobileRecorder />} />
          <Route path="/library" element={<RecordingsLibrary />} />
          <Route path="/recordings" element={<RecordingsLibrary />} />
          <Route path="/library/:id" element={<RecordingsLibrary />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </MobileLayout>
    );
  }

  // Desktop routes with desktop layout
  return (
    <Layout>
      <Routes>
        {/* Desktop Marketing */}
        <Route path="/welcome" element={<LandingPageWithAudio />} />
        
        {/* Legacy Desktop Routes */}
        <Route path="/coach" element={<SelfTalkAI />} />
        <Route path="/demo" element={<SelfTalkAI />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<AuthPage />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/auth" element={<AuthPage />} />
        
        {/* Dev Routes */}
        <Route path="/app" element={<MainApp />} />
        <Route path="/architecture" element={<ArchitectureDiagram />} />
        <Route path="/structure" element={<ProjectStructure />} />
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="/analytics" element={<AnalyticsDashboard />} />
        <Route path="/test" element={<TestDashboard />} />
        <Route path="/drop" element={<TextDrop />} />
        
        {/* Fallback to mobile home */}
        <Route path="*" element={<MobileHome />} />
      </Routes>
    </Layout>
  );
}

export default App;